import { useEffect } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, articles } from '@/mocks/anime';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);
  const catName = currentCategory?.name || '动漫分类';
  const catDesc = currentCategory?.description || '按类型浏览国产动漫内容';

  return (
    <main className="pt-20 pb-10 md:pb-14 bg-background-50 min-h-screen">
      <SEO
        title={`${catName} - 51动漫官网 | 国产动漫分类浏览`}
        description={`51动漫官网${catName}分类：${catDesc}。浏览${catName}相关的国产动漫资讯、深度解析和作品推荐，51动漫网与51动漫网站国产精品入口。`}
        keywords={`51动漫官网,51动漫,${catName},国产动漫,动漫分类,51动漫网`}
        canonicalUrl={`https://www.51anime.com/category/${slug}`}
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51anime.com/' },
          { '@type': 'ListItem', position: 2, name: '动漫分类', item: 'https://www.51anime.com/category/rexue-zhandou' },
          { '@type': 'ListItem', position: 3, name: catName },
        ],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: `${catName} - 51动漫官网`,
        description: catDesc,
        url: `https://www.51anime.com/category/${slug}`,
        isPartOf: { '@type': 'WebSite', name: '51动漫官网', url: 'https://www.51anime.com/' },
      }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-300">动漫分类</span>
          {currentCategory && (
            <>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-100 font-medium">{currentCategory.name}</span>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3 mb-8 flex-wrap">
          {currentCategory && (
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
              <i className={`${currentCategory.icon} text-xl`}></i>
            </div>
          )}
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-foreground-50 font-heading">
              {currentCategory ? currentCategory.name : '动漫分类'}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-400 mt-1">
                {currentCategory.description} · 共{currentCategory.count}部作品
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 flex-nowrap">
          <div className="flex items-center gap-1 px-1 py-1 bg-background-100 rounded-full">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-400 hover:text-foreground-100 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {categoryArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group rounded-lg overflow-hidden bg-background-100 border border-background-200/60 hover:border-primary-500/30 transition-all duration-200 cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-400"
                    style={{ backgroundImage: `url(${article.coverImage})` }}
                  />
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-background-50/90 rounded-md">
                    <i className="ri-star-fill text-xs text-secondary-500"></i>
                    <span className="text-xs font-medium text-foreground-100">{article.rating}</span>
                  </div>
                </div>
                <div className="p-4">
                  <h2 className="text-sm md:text-base font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2">
                    {article.title}
                  </h2>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{article.publishDate}</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <p className="text-foreground-400 text-sm">该分类下暂无文章</p>
          </div>
        )}
      </div>
    </main>
  );
}